import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import useSupabase from '../../../hooks/useSupabase';

const VehicleDataCard = ({ 
  vehicleData, 
  onCreateOrder, 
  onSaveVehicle, 
  loading = false 
}) => {

  const { vehicles, carHouses } = useSupabase();
  const [carHouseOptions, setCarHouseOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectedCarHouse, setSelectedCarHouse] = useState(null);
  const [ownerInfo, setOwnerInfo] = useState({ name: '', phone: '', email: '' });
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);

  useEffect(() => {
    loadCarHouses();
  }, []);

  const loadCarHouses = async () => {
    try {
      const { data, error } = await carHouses?.getAll({ status: 'active' });
      
      if (error) {
        console.error('Error loading car houses:', error);
        return;
      }

      setCarHouseOptions(data || []);
    } catch (error) {
      console.error('Error loading car houses:', error);
    }
  };

  const handleSaveVehicle = async () => {
    if (!vehicleData || !selectedCarHouse) return;

    setSaving(true);
    try {
      const vehiclePayload = {
        vin: vehicleData?.vin,
        make: vehicleData?.make,
        model: vehicleData?.model,
        year: vehicleData?.year,
        vehicle_type: vehicleData?.vehicleType || 'sedan',
        engine_size: vehicleData?.engine,
        fuel_type: vehicleData?.fuelType || 'gasoline',
        transmission: vehicleData?.transmission || 'automatic',
        color: vehicleData?.color,
        car_house_id: selectedCarHouse,
        owner_name: ownerInfo?.name || '',
        owner_phone: ownerInfo?.phone || '',
        owner_email: ownerInfo?.email || '',
        additional_info: {
          scanned_at: new Date()?.toISOString(),
          original_data: vehicleData
        }
      };

      const { data, error } = await vehicles?.create(vehiclePayload);
      
      if (error) {
        throw error;
      }

      onSaveVehicle?.(data);
    } catch (error) {
      console.error('Error saving vehicle:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateOrder = () => {
    if (!vehicleData) return;
    // Use the onCreateOrder prop from parent component
    onCreateOrder?.();
  };

  if (!vehicleData) return null;

  const {
    vin,
    make,
    model,
    year,
    engine,
    transmission,
    fuelType,
    bodyType,
    color,
    doors,
    displacement,
    power,
    cylinders,
    driveType,
    trim,
    series,
    country,
    plant,
    imageUrl,
    capturedImage,
    isUnknown,
    error,
    source
  } = vehicleData;

  // Simplified - only show essential vehicle info
  const vehicleSpecs = [
    { label: 'Fahrzeug', value: `${make} ${model}`, icon: 'Car' },
    { label: 'VIN', value: vin, icon: 'Hash' }
  ];

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center mb-2">
              {isUnknown ? (
                <>
                  <Icon name="AlertTriangle" size={20} className="text-warning mr-2" />
                  <span className="text-sm font-medium text-warning">VIN nicht gefunden</span>
                </>
              ) : error ? (
                <>
                  <Icon name="XCircle" size={20} className="text-error mr-2" />
                  <span className="text-sm font-medium text-error">Fehler beim Abrufen der Daten</span>
                </>
              ) : (
                <>
                  <Icon name="CheckCircle" size={20} className="text-success mr-2" />
                  <span className="text-sm font-medium text-success">VIN erfolgreich erkannt</span>
                </>
              )}
            </div>
            <h3 className="text-xl font-bold text-foreground mb-1">
              {isUnknown ? 'Unbekanntes Fahrzeug' : `${make} ${model}`}
            </h3>
            <p className="text-muted-foreground">
              {isUnknown ? 'Fahrzeugdaten nicht in der Datenbank gefunden' : `Baujahr ${year}`}
            </p>
            <p className="text-xs text-muted-foreground font-mono mt-2 bg-muted px-2 py-1 rounded">
              {vin}
            </p>
            {source && (
              <p className="text-xs text-muted-foreground mt-1">
                Datenquelle: {source}
              </p>
            )}
            {error && (
              <p className="text-xs text-error mt-1">
                Fehler: {error}
              </p>
            )}
          </div>
          
          {(imageUrl || capturedImage) && (
            <div className="ml-4 flex-shrink-0">
              <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted">
                <Image
                  src={capturedImage || imageUrl}
                  alt={`${make} ${model}`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Vehicle Specifications */}
      <div className="p-6">
        <h4 className="text-sm font-semibold text-foreground mb-4">Fahrzeugdaten</h4>
        
        <div className="grid grid-cols-2 gap-4">
          {vehicleSpecs?.map((spec, index) => (
            <div key={index} className="flex items-center space-x-2">
              <Icon name={spec?.icon} size={16} className="text-muted-foreground flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground">{spec?.label}</p>
                <p className="text-sm font-medium text-foreground truncate">{spec?.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Action Buttons */}
      <div className="p-6 bg-muted border-t border-border">
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="default"
            onClick={handleCreateOrder}
            loading={isCreatingOrder || saving}
            disabled={!vehicleData}
            iconName="Plus"
            iconPosition="left"
            className="flex-1"
          >
            Auftrag erstellen
          </Button>
          
          <Button
            variant="outline"
            onClick={handleSaveVehicle}
            loading={saving}
            iconName="Save"
            iconPosition="left"
            className="flex-1"
          >
            VIN speichern
          </Button>
        </div>
        
        <p className="text-xs text-muted-foreground mt-3 text-center">
          Erstellen Sie einen neuen Serviceauftrag oder speichern Sie die VIN für später.
        </p>
      </div>
    </div>
  );
};

export default VehicleDataCard;